import React, { useMemo, useState } from 'react';
import P from 'prop-types';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import uuid from 'uuid/v4';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import CtaButton from './lib/cta-button';
import { group, wrapper } from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import UniversalContainer from '../universal-container/universal-container';

const defaultProps = {
  buttonText: 'Button',
  buttonType: 'button',
};

const UniversalButton = ({
  buttonURL,
  buttonText,
  textColorOptions,
  titleColorOptions,
  componentName,
  ctaExtraText,
  alignment,
  outputSample,
  buttonContainer,
  includeContainer,
  ...ctaButtonProps
}) => {
  // Only append trk params if this is not an #anchor link on the
  // current page.
  //
  // @todo in the future we can extract this to a new maybeSetTrk()
  //     util if there are multiple use cases.
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const componentTrkName = componentName || 'universal-button';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL &&
      setTrk({
        url: buttonURL,
        params: {
          [TrkTag.location]: componentTrkName,
          [TrkTag.element]: TrkElement.button,
          [TrkTag.copy]: buttonText,
        },
      });
  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    id: `${componentTrkName}-${trkId}`,
    elementName: componentTrkName,
  };
  const buttonProps = useMemo(
    () => ({
      ...ctaButtonProps,
      buttonText:
        !buttonText && outputSample ? defaultProps.buttonText : buttonText,
      buttonType:
        !ctaButtonProps?.buttonType ||
        (ctaButtonProps?.buttonText === 'none' && outputSample)
          ? defaultProps.buttonType
          : ctaButtonProps?.buttonType,
    }),
    [buttonText, ctaButtonProps, outputSample]
  );

  const buttonContainerProps = {
    ...buttonContainer,
    alignment: {
      ...buttonContainer?.alignment,
      type: 'flex',
      direction: 'column',
    },
  };

  return (
    <ElementImpression {...elementImpressionProps}>
      <div style={{ textAlign: alignment }}>
        {includeContainer ? (
          <UniversalContainer {...buttonContainerProps}>
            <CtaButton
              buttonURL={modifiedUrl}
              buttonClass="cta__button"
              {...buttonProps}
            />
            <UniversalText
              {...ctaExtraText}
              defaultOptions={{ text: { textType: 'text' } }}
            />
          </UniversalContainer>
        ) : (
          <React.Fragment>
            <CtaButton
              buttonURL={modifiedUrl}
              buttonClass="cta__button"
              {...buttonProps}
            />
            <UniversalText
              {...ctaExtraText}
              defaultOptions={{ text: { textType: 'text' } }}
            />
          </React.Fragment>
        )}
      </div>
    </ElementImpression>
  );
};

UniversalButton.displayName = 'UniversalButton';

UniversalButton.propTypes = {
  ...CtaButton.propTypes,
  componentName: P.string,
  alignment: P.oneOf(['left', 'center', 'right']),
  children: P.node,
};

UniversalButton.defaultProps = {
  ...CtaButton.defaultProps,
  componentName: 'universal-button',
  alignment: 'left',
  children: null,
};

/**
 * @param {*} componentDocument
 * @param {string} fieldLabel Field Label in the Editor
 * @param {Array<'displayStyle'|'disabled'|'loading'|'loadingMessage'|'alignment'>} allowedControlsList List of controls to be shown in the editor
 * @param {string} fieldName
 * @param {object} options
 * @returns
 */
UniversalButton.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Universal Button',
  allowedControlsList = [],
  fieldName = 'universalButton',
  options = {
    disableGroup: false,
    includeContainer: false,
  }
) => {
  const editorPanelOptions = [
    ...CtaButton.getEditingConfig(
      componentDocument,
      fieldName,
      allowedControlsList
    ),
    ...(options.includeContainer
      ? [
          ...UniversalContainer.getEditingConfig(
            componentDocument,
            'Container',
            ['padding', 'margin', 'alignment'],
            `${fieldName}.buttonContainer`,
            { disableGroup: true, includeContainer: true }
          ),
        ]
      : []),
  ];
  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

export default UniversalButton;
